import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiBase, withBase } from '../config/api';
import '../styles/admin.css';
import fallbackImage from '../assets/fallback.jpg';

// --- Reusable Components ---

const Message = ({ text, type }) => {
  if (!text) return null;
  return <div className={`alert alert-${type} mt-2`}>{text}</div>;
};

// This form stays at the top of the page
const CreateCollectionForm = ({ onCollectionCreated }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('saree');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) {
      setError('Collection title is required.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${getApiBase()}/api/collections`, {
        title: title,
        category_key: category,
      });
      setTitle('');
      setCategory('saree'); // Reset category
      setLoading(false);
      setSuccess(`Collection "${title}" created successfully!`);
      onCollectionCreated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create collection.');
      setLoading(false);
    }
  };

  return (
    <div className="admin-card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Create New Collection</h5>
        <form onSubmit={handleSubmit} className="row g-3">
          <div className="col-md-6">
            <label htmlFor="collectionTitle" className="form-label">Collection Title</label>
            <input
              type="text"
              className="form-control"
              id="collectionTitle"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Collection Bollywood"
            />
          </div>
          <div className="col-md-4">
            <label htmlFor="collectionCategory" className="form-label">Category</label>
            <select
              id="collectionCategory"
              className="form-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="saree">Saree</option>
              <option value="lehenga">Lehenga</option>
              <option value="readymade">Readymade</option>
            </select>
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <button type="submit" className="btn btn-admin-primary w-100" disabled={loading}>
              {loading ? '...' : 'Create'}
            </button>
          </div>
        </form>
        {error && <Message text={error} type="danger" />}
        {success && <Message text={success} type="success" />}
      </div>
    </div>
  );
};

// --- MODAL COMPONENTS (NEW STRUCTURE) ---

const ImageUploadForm = ({ collectionId, onImageChange }) => {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    setFiles(e.target.files);
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    if (!files || files.length === 0) {
      setError('Please select one or more images to upload.');
      return;
    }
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('images', files[i]);
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.post(`${getApiBase()}/api/collections/${collectionId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess(res.data.message || `${files.length} images uploaded.`);
      onImageChange();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload images.');
    } finally {
      setLoading(false);
      setFiles(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-bold">Add Images to this Collection</label>
      <div className="input-group">
        <input
          type="file"
          className="form-control"
          id={`file-input-${collectionId}`}
          ref={fileInputRef}
          accept="image/*"
          multiple
          onChange={handleFileChange}
        />
        <button
          className="btn btn-admin-primary"
          type="button"
          onClick={handleUpload}
          disabled={loading}
        >
          {loading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
      <small className="text-muted">You can select multiple images at once. Max 5MB each.</small>
      {error && <Message text={error} type="danger" />}
      {success && <Message text={success} type="success" />}
    </div>
  );
};

const ReplaceButton = ({ imageId, onImageChange }) => {
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleReplace(file);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleReplace = async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      await axios.put(`${getApiBase()}/api/images/${imageId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onImageChange();
    } catch (err) {
      alert('Failed to replace image: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => fileInputRef.current.click();

  return (
    <>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      <button
        className="btn btn-sm btn-outline-secondary"
        onClick={handleClick}
        disabled={loading}
        title="Replace Image"
      >
        {loading ? '...' : '🔄'}
      </button>
    </>
  );
};

const ImagesTable = ({ images, onImageChange }) => {
  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await axios.delete(`${getApiBase()}/api/images/${imageId}`);
        onImageChange();
      } catch (err) {
        alert('Failed to delete image.');
      }
    }
  };

  if (images.length === 0) {
    return <p className="text-muted small">No images in this collection yet.</p>;
  }

  return (
    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <table className="table table-sm table-striped align-middle">
        <thead>
          <tr>
            <th style={{width: '120px'}}>Image</th>
            <th>Label (Filename)</th>
            <th style={{width: '120px'}}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {images.map((img) => (
            <tr key={img.id}>
              <td>
                <img
                  src={withBase(img.src)}
                  className="img-fluid rounded"
                  alt={img.label}
                  style={{ height: '60px', width: '100px', objectFit: 'cover' }}
                />
              </td>
              <td className="text-muted small" style={{wordBreak: 'break-all'}}>{img.label}</td>
              <td>
                <ReplaceButton imageId={img.id} onImageChange={onImageChange} />
                <button
                  className="btn btn-sm btn-outline-danger ms-2"
                  onClick={() => handleDeleteImage(img.id)}
                  aria-label="Delete image"
                  title="Delete Image"
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- NEW: The Collection Management Modal ---
// --- Note the new `prod-admin-` class names ---
const ManageCollectionModal = ({ collection, onClose, onCollectionDeleted, onImageChange }) => {
  if (!collection) return null;

  const handleDeleteCollection = async () => {
    if (window.confirm(`Are you sure you want to delete "${collection.title}"? This will delete all its images.`)) {
      try {
        await axios.delete(`${getApiBase()}/api/collections/${collection.id}`);
        onCollectionDeleted(); // Tell parent to refresh
        onClose(); // Close the modal
      } catch (err) {
        alert('Failed to delete collection.');
      }
    }
  };

  return (
    <div className="prod-admin-modal-overlay" onClick={onClose}>
      <div className="prod-admin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="prod-admin-modal-header">
          <h5 className="prod-admin-modal-title">Manage: {collection.title}</h5>
          <button type="button" className="prod-admin-btn-close" onClick={onClose}></button>
        </div>
        <div className="prod-admin-modal-body">
          <ImageUploadForm collectionId={collection.id} onImageChange={onImageChange} />
          <hr />
          <h6 className="mb-3">Existing Images ({collection.images.length})</h6>
          <ImagesTable images={collection.images} onImageChange={onImageChange} />
        </div>
        <div className="prod-admin-modal-footer justify-content-between">
          <button className="btn btn-outline-danger" onClick={handleDeleteCollection}>
            Delete Entire Collection
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// --- Main Page Component (Refactored for Grid Layout) ---
const ManageProducts = () => {
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // --- NEW: State to manage the modal ---
  const [selectedCollection, setSelectedCollection] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiBase()}/api/products-all`);
      setProductData(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load product data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshData = async () => {
    try {
      const res = await axios.get(`${getApiBase()}/api/products-all`);
      setProductData(res.data);
      
      if (selectedCollection) {
        const allCollections = res.data.flatMap(cat => cat.subcollections);
        const updatedCollection = allCollections.find(col => col.id === selectedCollection.id);
        if (updatedCollection) {
          setSelectedCollection(updatedCollection);
        } else {
          setSelectedCollection(null);
        }
      }
    } catch (err) {
      console.error("Failed to refresh data:", err);
    }
  };

  const handleFullRefresh = () => {
    setLoading(true);
    fetchData();
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading products...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container py-5">
        <h1 className="display-4 fw-bold mb-3 admin-dashboard-title">Manage Products</h1>
        
        <CreateCollectionForm onCollectionCreated={handleFullRefresh} />
        
        {error && <Message text={error} type="danger" />}
        
        {productData.map((category) => (
          <div key={category.key} className="mb-5">
            <h3 className="mb-3" style={{ textTransform: 'capitalize', borderBottom: '2px solid #eee', paddingBottom: '10px' }}>
              {category.title}
            </h3>
            
            {category.subcollections.length === 0 ? (
              <p className="text-muted">No collections in this category yet.</p>
            ) : (
              // --- NEW: Grid Layout for Collections ---
              <div className="row g-3">
                {category.subcollections.map((collection) => {
                  const coverImage = collection.images.length > 0 
                    ? withBase(collection.images[0].src) 
                    : fallbackImage;
                  
                  return (
                    <div key={collection.id} className="col-md-4 col-lg-3">
                      {/* --- Note the new `prod-admin-` class name --- */}
                      <div 
                        className="prod-admin-grid-card" 
                        onClick={() => setSelectedCollection(collection)}
                      >
                        <img 
                          src={coverImage} 
                          alt={collection.title} 
                          className="img-fluid rounded-top" 
                          style={{height: '200px', width: '100%', objectFit: 'cover'}}
                        />
                        <div className="p-3">
                          <h6 className="mb-1" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {collection.title}
                          </h6>
                          <small className="text-muted">{collection.images.length} images</small>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* --- NEW: Render the Modal --- */}
      {selectedCollection && (
        <ManageCollectionModal
          collection={selectedCollection}
          onClose={() => setSelectedCollection(null)}
          onCollectionDeleted={handleFullRefresh} // Refresh all data on delete
          onImageChange={refreshData}     // Refresh data on image change
        />
      )}
    </>
  );
};

export default ManageProducts;